import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Save,
  FileText,
  AlertCircle,
  Rocket,
  CheckCircle2,
  Undo2,
  CircleDot,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { useTenantContext } from '~/lib/tenantContext';
import {
  useActiveSchema,
  useContentForEdit,
  useDiscardAllDrafts,
  useDiscardDraft,
  useDraftKeys,
  useDraftSummary,
  usePublishTenant,
  useUpsertContent,
  CmsConflictError,
  type EditBase,
} from '~/lib/cms-queries';
import { orderedEntries, validateSection } from '@focus/schema';
import type { SectionDef } from '@focus/schema';
import { SchemaForm } from '~/components/SchemaForm/SchemaForm';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { tenantHasCapability } from '~/lib/capabilities';
import { can } from '~/lib/permissions';
import { currentUser } from '~/lib/pb';
import styles from './_authed.cms.module.css';

export const Route = createFileRoute('/_authed/cms')({
  component: CmsPage,
});

/**
 * Fallback hardcoded de Life Focus cuando el schema no declara section_groups.
 * Para sitios nuevos, declarar `section_groups` en el schema directamente —
 * este fallback se va a deprecar cuando todos los tenants migren.
 */
const FALLBACK_GROUPS_LIFE_FOCUS: ReadonlyArray<{
  label: string;
  sections: readonly string[];
}> = [
  { label: 'Sitio (global)', sections: ['config'] },
  {
    label: 'Páginas',
    sections: ['home', 'planes', 'sistema_focus', 'nosotros', 'contacto', 'gracias'],
  },
  { label: 'Legales', sections: ['legal_privacidad', 'legal_terminos', 'legal_cookies'] },
  { label: 'Servicios', sections: ['servicios'] },
  { label: 'Portafolio', sections: ['testimonios', 'clientes'] },
  { label: 'Blog', sections: ['blog_index', 'blog_posts', 'temas'] },
];

function groupSections<T>(
  sections: Array<[string, T]>,
  schemaGroups: ReadonlyArray<{ label: string; sections: readonly string[] }> | undefined,
): Array<{ label: string; items: Array<[string, T]> }> {
  const remaining = new Map(sections);
  const groups: Array<{ label: string; items: Array<[string, T]> }> = [];
  const definition = schemaGroups ?? FALLBACK_GROUPS_LIFE_FOCUS;
  for (const g of definition) {
    const items: Array<[string, T]> = [];
    for (const k of g.sections) {
      const v = remaining.get(k);
      if (v) {
        items.push([k, v]);
        remaining.delete(k);
      }
    }
    if (items.length > 0) groups.push({ label: g.label, items });
  }
  if (remaining.size > 0) {
    groups.push({
      label: 'Otros',
      items: Array.from(remaining.entries()),
    });
  }
  return groups;
}

/**
 * Traduce el path técnico de un error de validación (ej "home.seo.description")
 * a un breadcrumb de labels legibles del schema (ej "SEO › Descripción") + la
 * key del campo top-level (para anclar el scroll: `cms-field-<topKey>`).
 * Los índices de lista (ej "items.0") se muestran como "#1". Si un segmento no
 * resuelve en el schema, usa la key cruda como fallback.
 */
function truncateLabel(s: string, n = 36): string {
  return s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;
}

/** Identifica un ítem de lista por su item_label o un campo título/nombre. */
function resolveItemLabel(listDef: unknown, item: unknown): string | null {
  if (!item || typeof item !== 'object') return null;
  const obj = item as Record<string, unknown>;
  const tmpl = (listDef as { item_label?: string } | null)?.item_label;
  if (tmpl) {
    const filled = tmpl
      .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, k: string) => {
        const v = k
          .split('.')
          .reduce<unknown>((acc, p) => (acc as Record<string, unknown> | undefined)?.[p], obj);
        return v != null ? String(v) : '';
      })
      .trim();
    if (filled) return truncateLabel(filled);
  }
  for (const k of ['titulo', 'title', 'nombre', 'cliente', 'autorNombre', 'pregunta', 'slug']) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return truncateLabel(v);
  }
  return null;
}

function fieldLabelPath(
  section: SectionDef,
  errorPath: string,
  data?: unknown,
): { label: string; topKey: string } {
  // Tokeniza manejando índices de lista: "blog_posts[2].seo.description"
  // → ['blog_posts', {idx:2}, 'seo', 'description'].
  const tokens: Array<string | { idx: number }> = [];
  for (const seg of errorPath.split('.')) {
    const m = seg.match(/^([^[]*)((?:\[\d+\])*)$/);
    if (!m) {
      tokens.push(seg);
      continue;
    }
    if (m[1]) tokens.push(m[1]);
    for (const b of m[2].match(/\d+/g) ?? []) tokens.push({ idx: Number(b) });
  }
  // Ancla: el primer campo top-level después de la sección (los ítems de lista
  // no tienen ancla propia → cae a no-op de scroll, pero el breadcrumb sí lo nombra).
  const topKey = (tokens.slice(1).find((t) => typeof t === 'string') as string) ?? '';
  const labels: string[] = [];
  let cursor: unknown = section;
  let dataCursor: unknown = data;
  for (let i = 1; i < tokens.length; i++) {
    const tok = tokens[i];
    if (typeof tok === 'object') {
      const itemData = Array.isArray(dataCursor) ? dataCursor[tok.idx] : undefined;
      labels.push(resolveItemLabel(cursor, itemData) ?? `#${tok.idx + 1}`);
      dataCursor = itemData;
      continue; // cursor sigue siendo la lista; el próximo campo usa su item_schema
    }
    const c = cursor as {
      fields?: Record<string, { label?: string }>;
      item_schema?: Record<string, { label?: string }>;
    } | null;
    const fields = c?.fields ?? c?.item_schema ?? null;
    const def = fields ? fields[tok] : undefined;
    if (def) {
      labels.push(def.label || tok);
      cursor = def;
    } else {
      labels.push(tok);
      cursor = null;
    }
    dataCursor = (dataCursor as Record<string, unknown> | undefined)?.[tok];
  }
  return { label: labels.join(' › ') || errorPath, topKey };
}

function CmsPage() {
  const { selectedTenant } = useTenantContext();
  const schemaQ = useActiveSchema();
  const editQ = useContentForEdit();
  const draftKeysQ = useDraftKeys();

  const schema = schemaQ.data?.schema ?? null;
  const sections = schema ? orderedEntries(schema.sections, schema.section_order) : [];
  const groupedSections = schema ? groupSections(sections, schema.section_groups) : [];
  const draftKeys = new Set(draftKeysQ.data ?? []);

  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Estado de apertura de cada grupo del sidebar. Arranca TODO cerrado en cada
  // carga/refresco (no se persiste): el usuario abre solo lo que necesita.
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    // Al cambiar de cliente, colapsar todo de nuevo.
    setGroupOpen({});
  }, [selectedTenant?.id]);

  function toggleGroup(label: string) {
    setGroupOpen((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  // Grupo abierto solo si el usuario lo abrió (no se fuerza por sección activa,
  // para que el menú quede cerrado al cargar).
  function isGroupOpen(group: { label: string; items: Array<[string, unknown]> }): boolean {
    return groupOpen[group.label] === true;
  }

  // Cuando cambia el tenant o llega el schema, default a la primera sección.
  useEffect(() => {
    if (!activeKey && sections.length > 0) {
      const first = sections[0];
      if (first) setActiveKey(first[0]);
    }
    if (activeKey && !sections.find(([k]) => k === activeKey)) {
      const first = sections[0];
      setActiveKey(first ? first[0] : null);
    }
  }, [activeKey, sections]);

  // Gating por rol: sales_rep no administra el CMS (done-for-you / config).
  if (!can(currentUser()?.role, 'cms')) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          No tienes acceso al CMS. Esta sección es para administradores del cliente.
        </div>
      </div>
    );
  }

  if (!selectedTenant) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>Selecciona un cliente en el switcher del topbar.</div>
      </div>
    );
  }

  if (schemaQ.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>Cargando schema…</div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <div className={styles.header__title}>
            <h1 className={styles.title}>Sitio web</h1>
          </div>
        </header>
        <div className={styles.empty}>
          <AlertCircle size={32} aria-hidden style={{ marginBottom: 12 }} />
          <p>
            {currentUser()?.role === 'superadmin' ? (
              <>
                Este cliente todavía no tiene el sitio configurado.
                <br />
                Créalo con el script <code>cms:upsert-schema</code>.
              </>
            ) : (
              <>
                El sitio web todavía no está listo. Cuando Life Focus lo active, vas a poder
                editarlo desde aquí.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.header__title}>
          <h1 className={styles.title}>Sitio web</h1>
          <p className={styles.subtitle}>
            Guarda un borrador (solo tú lo ves). Publicar lo deja visible en el sitio.
            {draftKeys.size > 0 && (
              <>
                {' · '}
                <span className={styles.subtitle__changes}>
                  <CircleDot size={12} aria-hidden />
                  {draftKeys.size} {draftKeys.size === 1 ? 'sección' : 'secciones'} en borrador
                </span>
              </>
            )}
            {tenantHasCapability(selectedTenant, 'ads_google') && (
              <>
                {' · '}
                <Link to="/reports" search={{ tab: 'google_ads' }} className={styles.subtitleLink}>
                  Ver Google Ads
                </Link>
              </>
            )}
          </p>
        </div>
        <div className={styles.header__actions}>
          {draftKeys.size > 0 && <DiscardAllButton />}
          <PublishButton tenantSlug={selectedTenant.slug} />
        </div>
      </header>

      <div className={styles.layout}>
        <nav className={styles.sectionList}>
          {groupedSections.map((group) => {
            const open = isGroupOpen(group);
            const groupHasDraft = group.items.some(([k]) => draftKeys.has(k));
            return (
              <div key={group.label} className={styles.sectionGroup}>
                <button
                  type="button"
                  className={styles.sectionGroup__toggle}
                  onClick={() => toggleGroup(group.label)}
                  aria-expanded={open}
                >
                  {open ? (
                    <ChevronDown size={12} aria-hidden />
                  ) : (
                    <ChevronRight size={12} aria-hidden />
                  )}
                  <span className={styles.sectionGroup__label}>{group.label}</span>
                  <span className={styles.sectionGroup__count}>{group.items.length}</span>
                  {!open && groupHasDraft && (
                    <span
                      className={styles.sectionListItem__dot}
                      title="Hay cambios sin publicar en este grupo"
                      aria-label="Hay cambios sin publicar en este grupo"
                    />
                  )}
                </button>
                {open && (
                  <div className={styles.sectionGroup__items}>
                    {group.items.map(([key, def]) => (
                      <button
                        key={key}
                        type="button"
                        className={
                          key === activeKey
                            ? `${styles.sectionListItem} ${styles.sectionListItem__active}`
                            : styles.sectionListItem
                        }
                        onClick={() => setActiveKey(key)}
                      >
                        <FileText size={14} aria-hidden className={styles.sectionListItem__icon} />
                        <span className={styles.sectionListItem__label}>{def.label || key}</span>
                        {draftKeys.has(key) && (
                          <span
                            className={styles.sectionListItem__dot}
                            title="Tiene cambios sin publicar"
                            aria-label="Tiene cambios sin publicar"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {activeKey && schema.sections[activeKey] && (
          <SectionEditor
            key={`${selectedTenant.id}/${activeKey}`}
            sectionKey={activeKey}
            section={schema.sections[activeKey]!}
            base={editQ.data?.get(activeKey) ?? null}
            hasDraft={draftKeys.has(activeKey)}
          />
        )}
      </div>
    </div>
  );
}

interface SectionEditorProps {
  sectionKey: string;
  section: SectionDef;
  base: EditBase | null;
  hasDraft: boolean;
}

function SectionEditor({ sectionKey, section, base, hasDraft }: SectionEditorProps) {
  const upsert = useUpsertContent();
  const discard = useDiscardDraft();
  const confirm = useConfirm();

  // Inicializa el editor con el contenido vigente (draft si existe, sino published).
  const initialValue = base?.data ?? (section.type === 'list' ? [] : {});
  const [draft, setDraft] = useState<unknown>(initialValue);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-sincronizar SOLO cuando el server tiene una versión realmente distinta
  // (save/discard/publish bumpean `updated`; crear/descartar borrador cambia
  // `isDraft`). Depender del objeto `base` rompía la edición: cualquier refetch
  // (p.ej. al subir una imagen, que invalida la query) llega como referencia
  // nueva con la MISMA data y pisaba las ediciones locales — los items borrados
  // reaparecían. Por eso dependemos de los valores estables, no de la referencia.
  useEffect(() => {
    setDraft(base?.data ?? (section.type === 'list' ? [] : {}));
    setSavedAt(null);
    setShowErrors(false);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey, base?.updated, base?.isDraft, section.type]);

  const errors = useMemo(
    () => validateSection(section, draft, sectionKey),
    [section, draft, sectionKey],
  );
  // Bloqueantes (campo obligatorio vacío, tipo, formato) vs advertencias
  // (largo recomendado de SEO, etc.). Solo las bloqueantes impiden guardar.
  const blocking = useMemo(() => errors.filter((e) => e.severity !== 'warning'), [errors]);
  const warnings = useMemo(() => errors.filter((e) => e.severity === 'warning'), [errors]);

  function handleSave(force = false) {
    setSaveError(null);
    if (blocking.length > 0) {
      setShowErrors(true);
      return;
    }
    upsert.mutate(
      {
        sectionKey,
        data: draft as Record<string, unknown>,
        baseUpdated: base?.updated ?? null,
        baseIsDraft: base?.isDraft ?? false,
        force,
      },
      {
        onSuccess: () => {
          setSavedAt(new Date());
          setShowErrors(false);
        },
        onError: async (err) => {
          // Conflicto de concurrencia: otra persona guardó esta sección desde que
          // la abriste → ofrecer sobrescribir en vez de pisar en silencio.
          if (err instanceof CmsConflictError) {
            const ok = await confirm({
              title: 'Otro editor cambió esta sección',
              description: `${err.editorName} guardó cambios en esta sección desde que la abriste. Si guardas ahora, se pierde su trabajo. ¿Sobrescribir de todas formas? (Si cancelas, tus cambios siguen acá; puedes Descartar para traer la versión actual.)`,
              confirmLabel: 'Sobrescribir',
              tone: 'warning',
            });
            if (ok) handleSave(true);
            return;
          }
          // Cualquier otro fallo: avisar (nunca en silencio).
          setSaveError(
            err instanceof Error && err.message
              ? err.message
              : 'No se pudo guardar. Revisa tu conexión y reintenta.',
          );
        },
      },
    );
  }

  async function handleDiscard() {
    if (!hasDraft) return;
    const ok = await confirm({
      title: 'Descartar cambios',
      description:
        'Vas a descartar los cambios de esta sección y volver al último contenido publicado.',
      confirmLabel: 'Descartar',
      tone: 'warning',
    });
    if (!ok) return;
    discard.mutate(sectionKey);
  }

  return (
    <div className={styles.editor}>
      <div className={styles.editor__header}>
        <div className={styles.editor__heading}>
          <h2 className={styles.editor__title}>{section.label || sectionKey}</h2>
          <p className={styles.editor__status}>
            {hasDraft ? (
              <span className={styles.editor__statusDraft}>
                <CircleDot size={12} aria-hidden /> Cambios sin publicar
              </span>
            ) : (
              <span className={styles.editor__statusPublished}>
                <CheckCircle2 size={12} aria-hidden /> Publicado
              </span>
            )}
            {savedAt && (
              <span className={styles.savedHint}>
                · borrador guardado {savedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className={styles.editor__actions}>
          {hasDraft && (
            <button
              type="button"
              className={styles.buttonGhost}
              onClick={handleDiscard}
              disabled={discard.isPending}
              title="Descartar el borrador y volver a lo publicado"
            >
              <Undo2 size={14} aria-hidden />
              Descartar
            </button>
          )}
          <button
            type="button"
            className={styles.button}
            onClick={() => handleSave()}
            disabled={upsert.isPending}
          >
            <Save size={14} aria-hidden />
            {upsert.isPending ? 'Guardando…' : 'Guardar borrador'}
          </button>
        </div>
      </div>

      {saveError && (
        <div className={styles.errorList}>
          <strong>No se pudo guardar:</strong> {saveError}
        </div>
      )}

      {showErrors && blocking.length > 0 && (
        <div className={styles.errorList}>
          <strong>
            {blocking.length === 1
              ? 'Revisa este campo antes de guardar:'
              : `Revisa estos ${blocking.length} campos antes de guardar:`}
          </strong>
          <ul>
            {blocking.slice(0, 8).map((err, i) => {
              const { label, topKey } = fieldLabelPath(section, err.path, draft);
              return (
                <li key={i}>
                  <button
                    type="button"
                    className={styles.errorLink}
                    onClick={() => {
                      const el = document.getElementById(`cms-field-${topKey}`);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      const input = el?.querySelector<HTMLElement>('input, textarea, select');
                      input?.focus({ preventScroll: true });
                    }}
                  >
                    {label}
                  </button>{' '}
                  — {err.message}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div className={styles.warnList}>
          <strong>
            {warnings.length === 1
              ? '1 campo supera el largo recomendado (SEO).'
              : `${warnings.length} campos superan el largo recomendado (SEO).`}
          </strong>{' '}
          Puedes guardar igual — es solo una recomendación de optimización.
        </div>
      )}

      <SchemaForm section={section} value={draft} onChange={setDraft} />
    </div>
  );
}

// ── Discard all + Publish buttons ──────────────────────────────────────

function DiscardAllButton() {
  const discardAll = useDiscardAllDrafts();
  const confirm = useConfirm();
  const [doneCount, setDoneCount] = useState<number | null>(null);

  // Prevenir refresh / navegación mientras está borrando — los DELETEs
  // pendientes se cancelan si la página se descarga.
  useEffect(() => {
    if (!discardAll.isPending) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [discardAll.isPending]);

  // Hint efímero al completar — desaparece a los 4s.
  useEffect(() => {
    if (doneCount === null) return;
    const t = setTimeout(() => setDoneCount(null), 4000);
    return () => clearTimeout(t);
  }, [doneCount]);

  async function handleClick() {
    if (discardAll.isPending) return;
    const ok = await confirm({
      title: 'Descartar todos los borradores',
      description:
        'Esto descarta TODOS los borradores sin publicar del cliente y vuelve al último contenido publicado. La acción no se puede deshacer.',
      confirmLabel: 'Descartar todo',
      tone: 'danger',
    });
    if (!ok) return;
    discardAll.mutate(undefined, {
      onSuccess: (n) => setDoneCount(n),
    });
  }

  const isPending = discardAll.isPending;

  return (
    <div className={styles.discardWrap}>
      <button
        type="button"
        className={styles.buttonGhost}
        onClick={handleClick}
        disabled={isPending}
        title="Descartar todos los cambios sin publicar"
        aria-busy={isPending}
      >
        {isPending ? (
          <>
            <Loader2 size={14} aria-hidden className={styles.spin} />
            Descartando…
          </>
        ) : (
          <>
            <Undo2 size={14} aria-hidden />
            Descartar todo
          </>
        )}
      </button>
      {doneCount !== null && (
        <span className={styles.discardHint} role="status">
          <CheckCircle2 size={12} aria-hidden />
          {doneCount === 0
            ? 'Sin borradores pendientes'
            : `${doneCount} ${doneCount === 1 ? 'borrador descartado' : 'borradores descartados'}`}
        </span>
      )}
    </div>
  );
}

function PublishButton({ tenantSlug }: { tenantSlug: string }) {
  const publish = usePublishTenant();
  const summaryQ = useDraftSummary();
  const confirm = useConfirm();

  async function handleClick() {
    if (publish.isPending) return;
    const items = summaryQ.data ?? [];
    const description =
      items.length === 0 ? (
        <>
          No hay borradores pendientes — esta acción solo regenera el deploy con el contenido
          actualmente publicado. Útil si cambiaste estructura del repo o el SDK.
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>
            Vas a publicar {items.length} {items.length === 1 ? 'sección' : 'secciones'} con
            cambios. CF Pages despliega en ~30-60 segundos:
          </p>
          {items.some((it) => it.byOther) && (
            <p
              style={{
                margin: '10px 0 0',
                padding: '8px 10px',
                borderRadius: 8,
                background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                color: 'var(--color-warning-strong, var(--color-warning))',
                fontSize: '0.88em',
              }}
            >
              ⚠ Algunas secciones las editó otra persona — al publicar también subes sus cambios.
              Revísalas antes de continuar.
            </p>
          )}
          <ul style={{ margin: '12px 0 0', paddingLeft: 20, fontSize: '0.9em' }}>
            {items.map((it) => (
              <li key={it.section_key} style={{ marginBottom: 4 }}>
                <strong>{it.label}</strong>
                {' — '}
                <span style={{ opacity: 0.75 }}>{it.summary}</span>
                {it.kind === 'new' && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: 'rgb(34, 139, 84)',
                      fontSize: '0.85em',
                    }}
                  >
                    nuevo
                  </span>
                )}
                {it.byOther && it.editedBy && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: 'var(--color-warning-strong, var(--color-warning))',
                      fontSize: '0.85em',
                    }}
                  >
                    · editado por {it.editedBy}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      );
    const ok = await confirm({
      title: items.length === 0 ? 'Re-publicar sin cambios' : 'Publicar al sitio',
      description,
      confirmLabel: items.length === 0 ? 'Re-publicar' : 'Publicar',
      tone: 'primary',
    });
    if (!ok) return;
    publish.mutate(tenantSlug);
  }

  return (
    <div className={styles.publishWrap}>
      <button
        type="button"
        className={styles.publishButton}
        onClick={handleClick}
        disabled={publish.isPending}
        aria-busy={publish.isPending}
      >
        {publish.isPending ? (
          <Loader2 size={14} aria-hidden className={styles.spin} />
        ) : (
          <Rocket size={14} aria-hidden />
        )}
        {publish.isPending ? 'Publicando…' : 'Publicar'}
      </button>
      {publish.isSuccess && publish.data && (
        <span className={styles.publishHint}>
          <CheckCircle2 size={12} aria-hidden />
          {publish.data.changes === 'committed'
            ? `Commit ${publish.data.commit_sha?.slice(0, 7)} · CF Pages despliega en ~30-60s`
            : 'Sin cambios desde el último publish'}
        </span>
      )}
      {publish.isError && (
        <span className={styles.publishError}>
          ✗ {(publish.error as Error)?.message ?? 'Error al publicar'}
        </span>
      )}
    </div>
  );
}
